import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { CardDesignData, CardFormat, CardText, ProductUid, QrConfig } from '@flashcard/shared';

interface DesignState extends CardDesignData {
  productUid: ProductUid | null;
  setProductUid: (uid: ProductUid) => void;
  setFormat: (f: CardFormat) => void;
  setBackgroundColor: (c: string) => void;
  addText: (t: CardText) => void;
  updateText: (id: string, patch: Partial<CardText>) => void;
  removeText: (id: string) => void;
  setLogo: (url: string, box: { x: number; y: number; width: number; height: number }) => void;
  setQrCode: (q: QrConfig | null) => void;
  loadDesign: (d: CardDesignData) => void;
  reset: () => void;
}

const initialState: CardDesignData = {
  format: 'standard',
  backgroundColor: '#ffffff',
  texts: [],
  logoUrl: null,
  logoBox: null,
  qrCode: null,
};

const DesignContext = createContext<DesignState | null>(null);

export function DesignProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CardDesignData>(initialState);
  const [productUid, setProductUidState] = useState<ProductUid | null>(null);

  const setProductUid = useCallback((uid: ProductUid) => setProductUidState(uid), []);
  const setFormat = useCallback((f: CardFormat) => setState((s) => ({ ...s, format: f })), []);
  const setBackgroundColor = useCallback((c: string) => setState((s) => ({ ...s, backgroundColor: c })), []);
  const addText = useCallback((t: CardText) => setState((s) => ({ ...s, texts: [...s.texts, t] })), []);
  const updateText = useCallback(
    (id: string, patch: Partial<CardText>) =>
      setState((s) => ({ ...s, texts: s.texts.map((t) => (t.id === id ? { ...t, ...patch } : t)) })),
    [],
  );
  const removeText = useCallback(
    (id: string) => setState((s) => ({ ...s, texts: s.texts.filter((t) => t.id !== id) })),
    [],
  );
  const setLogo = useCallback(
    (url: string, box: { x: number; y: number; width: number; height: number }) =>
      setState((s) => ({ ...s, logoUrl: url, logoBox: box })),
    [],
  );
  const setQrCode = useCallback((q: QrConfig | null) => setState((s) => ({ ...s, qrCode: q })), []);
  const loadDesign = useCallback((d: CardDesignData) => setState(d), []);
  const reset = useCallback(() => {
    setState(initialState);
    setProductUidState(null);
  }, []);

  return (
    <DesignContext.Provider
      value={{
        ...state,
        productUid,
        setProductUid,
        setFormat,
        setBackgroundColor,
        addText,
        updateText,
        removeText,
        setLogo,
        setQrCode,
        loadDesign,
        reset,
      }}
    >
      {children}
    </DesignContext.Provider>
  );
}

export function useDesign() {
  const ctx = useContext(DesignContext);
  if (!ctx) throw new Error('useDesign must be used within DesignProvider');
  return ctx;
}
